/**
* @file   src\components\OrderHardware.tsx
* @brief  OrderHardware component.
* @date   JUN, 2024
* @author ZCO Engineer
* @copyright (c) 2024, ZCO
*/
import { React, useState,  Button, useNavigate,  useRef, useEffect, useParams } from '../utils/thirdpartyComponents';
import { Stepper, Step } from 'react-form-stepper';
import OrderAddDevice from './OrderAddDevice';
import OrderAddPackage from './OrderAddPackage';
import OrderAddCart from './OrderAddCart';
import OrderShippingAddress from './OrderShippingAddress';
import OrderPayment from './OrderPayment';
import { pageURLs } from '../utils/pageUrls';
import { IDeviceData, IOrderData } from '../interfaces/hardwareInterface';
import { OrderHardwareApi } from '../utils/apiUrls';
import SmallLoader from './SmallLoader';
import axios from '../config/axiosConfig';
import { MessageToaster } from '../utils/toastUtil';


const OrderHardware = ({ onComplete }: { onComplete: () => void }) => {
    // Toast object creation.
    const toast = new MessageToaster();
    // const initialFee = 2
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<{ [key: number]: boolean }>({
        0: false,
        1: false,
        2: false,
        3: false,
        4: false
    });
    const param = useParams();
    const current_carwash_id = param.id ? parseInt(param.id) : 0;;
    const [packageAdded, setPackageAdded] = useState<number>(0);
    const [initialFee, setInitialFee] = useState<number>(0);
    const [orderData, setOrderData] = useState<IOrderData>({
        devices: {},
        packageItems: null,
        shippingAddress: "",
        billingAddress: "",
        sameAsBillingAddress: false,
        payment: null,
        initialFee:initialFee,
        grandTotal: 0,
        carWashId:current_carwash_id
    });
    const [isPaymentValid, setIsPaymentValid] = useState<boolean>(false);
    const navigate = useNavigate();
    const paymentRef = useRef<{ validateForm: () => void } | null>(null);

   

    const steps = ['Add Devices', 'Add Marketing Package', 'Cart', 'Shipping Address', 'Payment'];

    useEffect(() => {
        getInitialFee()
        getPackageList()
        
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeStep]);




const getInitialFee = async () => {
    try {
        setLoading(true); // Show loader
        const { data } = await axios.get(OrderHardwareApi.CARWASH_OWNER_ORDER_SETTINGS);
        if (data.error === 0) {
        setInitialFee(data.data.initialFee)
        setOrderData((prevData) => ({
            ...prevData,
            initialFee:data.data.initialFee
        }));
      }
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
      finally{
        setLoading(false);
      }
}

    const handleNext = async () => {
        if (activeStep === 4) {
            if (paymentRef.current) {
                paymentRef.current.validateForm();
            }
            if (!isPaymentValid) {
                return;
            }
        }
        else if(activeStep === 3)
        {
            if(orderData.shippingAddress == '')
            {
                toast.toastError("Please add shipping address")
                return false
            }
            else if(orderData.billingAddress == '')
            {
                toast.toastError("Please add billing address")
                return false
            }
        }

        setCompletedSteps((prev) => ({ ...prev, [activeStep]: true }));
        if (activeStep === steps.length - 1) {
            navigate("/" + pageURLs.payment_success);
        } 
        else if(packageAdded == 0 && activeStep === 0){
            setActiveStep(2)
        }

        else{
            
            setActiveStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
        }
    };

    const handleStepClick = (step: number) => {
        if (completedSteps[step]) {
            setActiveStep(step);
        }
    };

    // Function to delete an item
    const deleteItem = (id: string) => {
        const updatedDevices = { ...orderData.devices };
        delete updatedDevices[id];
        updateGrandTotal(updatedDevices);
    };

    
    // Function to delete an item
    const deletePackage = (item:any) => {
        
        setOrderData((prevData) => {
            const grandTotal = typeof prevData.grandTotal === "number" ? prevData.grandTotal : 0;
            const packagePrice = typeof item.packagePrice === "number" ? item.packagePrice : 0;
        
            return {
                ...prevData,
                packageItems: null,
                grandTotal: grandTotal - packagePrice
            };
        });
    };


    const getPackageList = async () => {
        try {
          setLoading(true); 
            const { data } = await axios.get(OrderHardwareApi.CARWASH_OWNER_LIST_PACKAGE);
            if (data.error === 0) {
                 setPackageAdded(data?.data?.length || 0);
            }
   
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
        finally{
          setLoading(false);
        }
    };
  


    // Function to update quantity
    const updateQuantity = (id: string, quantity: number) => {
        const updatedDevices = { ...orderData.devices };
        if (updatedDevices[id]) {
            updatedDevices[id].quantity = quantity;
        }
        updateGrandTotal(updatedDevices);
    };

    // Function to calculate and update grand total
    const updateGrandTotal = (devices: IOrderData['devices']) => {
        const grandTotal = Object.values(devices).reduce(
            (total, device) => total + device.quantity * device.price,
            0
        );
        setOrderData((prevData) => ({
            ...prevData,
            devices,
            grandTotal: prevData?.packageItems?.packagePrice ? grandTotal + prevData?.packageItems?.packagePrice : grandTotal
        }));
    };

    return (
        <div className='step-content-outer'>
            <div className='common-wrap location-info-wrapper hardware-order-wrap'>
                {/* <Button variant="light" className="light-btn back-btn">
                    <img src={Backicon} alt="Back" />
                </Button> */}
                <div className="title-block mx-auto text-center">
                    <h3 className='f-bold'>Order Hardware</h3>
                    <p className='f-medium'>Speed up the process of delivering hardware to your locations in a single step.</p>
                </div>

                <Stepper activeStep={activeStep} className='stepper-main' stepClassName='single-step'>
                    {steps.map((label, index) => (
                        <Step
                            key={label}
                            label={label}
                            onClick={() => handleStepClick(index)}
                            style={{ cursor: completedSteps[index] ? 'pointer' : 'not-allowed' }}
                        />
                    ))}
                </Stepper>

                {activeStep === 0 && (
                    <OrderAddDevice cartData={orderData} onSelectionChange={(devices) => {
                        const newTotal = Object.values(devices).reduce(
                            (total, device) => total + device.price * device.quantity,
                            0
                        )
                        setOrderData((prevData) => ({
                            ...prevData,
                            devices,
                            grandTotal:newTotal
                        }));
                    }} />
                )}

                {activeStep === 1 && (
                    <OrderAddPackage cartData={orderData} onPackageAdd={(item) => {
                        setOrderData((prevData) => ({
                            ...prevData,
                            packageItems: item,
                            grandTotal:prevData.grandTotal + item.packagePrice
                        }));
                    }} />
                )}

                {activeStep === 2 && (
                    <OrderAddCart
                        cartData={orderData}
                        deleteItem={deleteItem}
                        deletePackage={deletePackage}
                        updateQuantity={updateQuantity}
                    />
                )}

                {activeStep === 3 && <OrderShippingAddress onAddressAdd={(billingAddress:string,shippingAddress:string) => {
                        setOrderData((prevData) => ({
                            ...prevData,
                            billingAddress: billingAddress,
                            shippingAddress:shippingAddress
                        }));
                    }} />}

                {activeStep === 4 && (
                    <OrderPayment onPaymentComplete={setIsPaymentValid}  cartData={orderData} />
                )}
            </div>

            <div className='btn-action-sec d-flex align-items-center justify-content-between'>
                {/* <Link to={""} className='f-bold'>Skip to Next</Link> */}
                {activeStep != 4 && <Button
                    variant="primary"
                    className="primary-btn ms-auto"
                    onClick={handleNext}
                    disabled={
                        (activeStep === 0 && !Object.values(orderData.devices).some((device: IDeviceData) => device.quantity > 0))
                        || (activeStep === 2 && (Object.keys(orderData.devices).length === 0 && orderData.packageItems==null))
                    }
                    title='Next'
                >
                    Next
                </Button>}
            </div>
            {loading && <SmallLoader />}
        </div>
    );
};

export default OrderHardware;
